import "./PoolSelect.scss"
import React, { useState, useEffect, useMemo } from "react"
import { Box } from "@mui/material"
import { POOLS_MAP, Pool } from "src/constants"
import { MAX_POOL_SELECTABLE } from "src/constants/boostFarm"
import { getChainId } from "src/utils"
import useFarmBoostHandler from "src/hooks/BoostFarming/useFarmBoostHandler"

import Text from "../Text"
import Button from "../Button"
import { PoolItem } from "./PoolItem"

interface IPoolItem extends Pool {
  pid: number
  selected: boolean
}

interface PoolSelectorViewProps {
  boostedPoolIds?: number[]
  selectedPoolIds?: number[]
  onChange?: (newSelectedPools: IPoolItem[]) => void
  onClose?: () => void
}

const PoolSelectorView: React.FC<PoolSelectorViewProps> = ({
  boostedPoolIds,
  selectedPoolIds,
  onChange,
  onClose,
}) => {
  const chainId = getChainId()
  const [poolList, setPoolList] = useState<IPoolItem[]>([])
  const { onBoostFarm } = useFarmBoostHandler()
  const selectedCount = poolList.filter((item) => item.selected).length
  const maxSelect = MAX_POOL_SELECTABLE - (boostedPoolIds?.length || 0)

  useEffect(() => {
    setPoolList(
      Object.values(POOLS_MAP)
        .filter(
          ({ addresses, rewardPids }) =>
            (chainId ? addresses[chainId] : false) &&
            !boostedPoolIds?.includes(rewardPids[chainId]!),
        )
        .map((pool) => {
          const pid = pool.rewardPids[chainId]!
          return {
            ...pool,
            pid,
            selected: selectedPoolIds && selectedPoolIds.includes(pid),
          } as IPoolItem
        }),
    )
  }, [chainId, selectedPoolIds])

  const renderContent = useMemo(() => {
    return poolList.map((item) => {
      return (
        <PoolItem
          key={item.name}
          poolName={item.name}
          selected={item.selected}
          disabled={!item.selected && selectedCount >= maxSelect}
          onClick={() => {
            if (selectedCount >= maxSelect && !item.selected) {
              return
            }
            const updatedPoolList = poolList.map((pool) => {
              if (pool.pid === item.pid) {
                return { ...pool, selected: !pool.selected }
              }
              return pool
            })
            setPoolList(updatedPoolList)
            const newSelectedPools = updatedPoolList.filter(
              (pool) => pool.selected,
            )
            onChange && onChange(newSelectedPools)
          }}
        />
      )
    })
  }, [maxSelect, poolList, onChange, selectedCount])

  return (
    <Box>
      <Box mb={3} className="divider" />
      <Text mb={3}>
        Choose from the following pools to which you have provided liquidity:
      </Text>
      <Box className="pool-wrapper">{renderContent}</Box>
      <Box my={3} className="divider" />
      <Box>
        <Text color="dim" size="micro" mb={3}>
          {selectedCount} out of {maxSelect} pools selected
        </Text>
        <Button
          disabled={selectedCount <= 0}
          fullWidth
          onClick={() => {
            selectedPoolIds &&
              onBoostFarm(selectedPoolIds).then(() => {
                onClose && onClose()
              })
          }}
        >
          Confirm
        </Button>
      </Box>
    </Box>
  )
}

export default PoolSelectorView
